import { IMAGE_PDF_SUPPORTED_FORMATS, IMAGE_SUPPORTED_FORMATS, TWO_MB } from '@src/constants';
import { Shape } from '@src/validations';
import * as yup from 'yup';
import { BusinessDocumentationFormData } from '../components/businessDocumentationStep';

export interface IBusinessDocumentDocs {
  name: string;
  label: string;
  removeStrict?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const numbersRegex = /^[0-9]+$/;

const otherDocumentsSchema = yup.object().shape({
  id: yup.string().required('ID is required'),
  documentName: yup.string().when('show', (show: boolean) => {
    return show ? yup.string().required('Please enter document name') : yup.string();
  }),
  document: yup
    .mixed()
    .nullable()
    .when('show', (show: boolean) => {
      return show
        ? yup
            .mixed()
            .nullable()
            .required('Please add document')
            .test(
              'FILE_FORMAT',
              'Please check the format of the files selected. The supported formats are jpeg, jpg or png.',
              (value) => {
                if (typeof value === 'string') {
                  return true;
                }

                if (value) {
                  if (!IMAGE_SUPPORTED_FORMATS.includes(value?.type)) {
                    return false;
                  }
                }

                return true;
              }
            )
            .test('FILE_SIZE', 'File too big. Maximum of 2mb', (value) => {
              if (typeof value === 'string') {
                return true;
              }

              if (value) {
                if (value.size > TWO_MB) {
                  return false;
                }
              }

              return true;
            })
        : yup.mixed().nullable();
    })
});

export const getBusinessDocumentationSchema = (requiredDocs: IBusinessDocumentDocs[]) => {
  const baseSchema: Record<string, any> = {
    agentOnboardingKybForm: yup
      .mixed()
      .nullable()
      .required('Please add document')
      .test(
        'FILE_FORMAT',
        'Please check the format of the files selected. The supported formats are jpeg, jpg or png.',
        (value) => {
          if (typeof value === 'string') {
            return true;
          }

          if (value) {
            if (!IMAGE_PDF_SUPPORTED_FORMATS.includes(value?.type)) {
              return false;
            }
          }

          return true;
        }
      )
      .test('FILE_SIZE', 'File too big. Maximum of 2mb', (value) => {
        if (typeof value === 'string') {
          return true;
        }

        if (value) {
          if (value.size > TWO_MB) {
            return false;
          }
        }

        return true;
      }),
    certificateOfIncorporation: yup
      .mixed()
      .nullable()
      .required('Please add document')
      .test(
        'FILE_FORMAT',
        'Please check the format of the files selected. The supported formats are jpeg, jpg or png.',
        (value) => {
          if (typeof value === 'string') {
            return true;
          }

          if (value) {
            if (!IMAGE_PDF_SUPPORTED_FORMATS.includes(value?.type)) {
              return false;
            }
          }

          return true;
        }
      )
      .test('FILE_SIZE', 'File too big. Maximum of 2mb', (value) => {
        if (typeof value === 'string') {
          return true;
        }

        if (value) {
          if (value.size > TWO_MB) {
            return false;
          }
        }

        return true;
      }),
    amlComplianceDocument: yup
      .mixed()
      .nullable()
      .required('Please add document')
      .test(
        'FILE_FORMAT',
        'Please check the format of the files selected. The supported formats are jpeg, jpg or png.',
        (value) => {
          if (typeof value === 'string') {
            return true;
          }

          if (value) {
            if (!IMAGE_PDF_SUPPORTED_FORMATS.includes(value?.type)) {
              return false;
            }
          }

          return true;
        }
      )
      .test('FILE_SIZE', 'File too big. Maximum of 2mb', (value) => {
        if (typeof value === 'string') {
          return true;
        }

        if (value) {
          if (value.size > TWO_MB) {
            return false;
          }
        }

        return true;
      }),
    agreement: yup.boolean().oneOf([true], 'Please kindly agree to the condition'),
    otherComplianceDocs: yup.array().of(otherDocumentsSchema),
    einCertificate: yup
      .mixed()
      .nullable()
      .required('Please add document')
      .test(
        'FILE_FORMAT',
        'Please check the format of the files selected. The supported formats are jpeg, jpg or png.',
        (value) => {
          if (typeof value === 'string') {
            return true;
          }

          if (value) {
            if (!IMAGE_PDF_SUPPORTED_FORMATS.includes(value?.type)) {
              return false;
            }
          }

          return true;
        }
      )
      .test('FILE_SIZE', 'File too big. Maximum of 2mb', (value) => {
        if (typeof value === 'string') {
          return true;
        }

        if (value) {
          if (value.size > TWO_MB) {
            return false;
          }
        }

        return true;
      }),
    irsCertificate: yup
      .mixed()
      .nullable()
      .test(
        'FILE_FORMAT',
        'Please check the format of the files selected. The supported formats are jpeg, jpg or png.',
        (value) => {
          if (typeof value === 'string') {
            return true;
          }

          if (value) {
            if (!IMAGE_PDF_SUPPORTED_FORMATS.includes(value?.type)) {
              return false;
            }
          }

          return true;
        }
      )
      .test('FILE_SIZE', 'File too big. Maximum of 2mb', (value) => {
        if (typeof value === 'string') {
          return true;
        }

        if (value) {
          if (value.size > TWO_MB) {
            return false;
          }
        }

        return true;
      }),
    certificateOfGoodStanding: yup
      .mixed()
      .nullable()
      .test(
        'FILE_FORMAT',
        'Please check the format of the files selected. The supported formats are jpeg, jpg or png.',
        (value) => {
          if (typeof value === 'string') {
            return true;
          }

          if (value) {
            if (!IMAGE_PDF_SUPPORTED_FORMATS.includes(value?.type)) {
              return false;
            }
          }

          return true;
        }
      )
      .test('FILE_SIZE', 'File too big. Maximum of 2mb', (value) => {
        if (typeof value === 'string') {
          return true;
        }

        if (value) {
          if (value.size > TWO_MB) {
            return false;
          }
        }

        return true;
      }),
    businessDba: yup
      .mixed()
      .nullable()
      .test(
        'FILE_FORMAT',
        'Please check the format of the files selected. The supported formats are jpeg, jpg or png.',
        (value) => {
          if (typeof value === 'string') {
            return true;
          }

          if (value) {
            if (!IMAGE_PDF_SUPPORTED_FORMATS.includes(value?.type)) {
              return false;
            }
          }

          return true;
        }
      )
      .test('FILE_SIZE', 'File too big. Maximum of 2mb', (value) => {
        if (typeof value === 'string') {
          return true;
        }

        if (value) {
          if (value.size > TWO_MB) {
            return false;
          }
        }

        return true;
      })
  };

  requiredDocs.forEach((doc) => {
    baseSchema[doc.name] = yup
      .mixed()
      .nullable()
      .test(
        'FILE_FORMAT',
        'Please check the format of the files selected. The supported formats are jpeg, jpg or png.',
        (value) => {
          if (typeof value === 'string') {
            return true;
          }

          if (value) {
            if (!IMAGE_PDF_SUPPORTED_FORMATS.includes(value?.type)) {
              return false;
            }
          }

          return true;
        }
      )
      .test('FILE_SIZE', 'File too big. Maximum of 2mb', (value) => {
        if (typeof value === 'string') {
          return true;
        }

        if (value) {
          if (value.size > TWO_MB) {
            return false;
          }
        }

        return true;
      })
      .test('IS_REQUIRED', `${doc.label} is required`, (value) => {
        if (doc.removeStrict) {
          return true;
        } else {
          if (!value) {
            return false;
          }
        }

        return true;
      });
    // .required(`${doc.label} is required`);
  });

  return yup.object().shape(baseSchema);
};

// export const getBusinessDocumentationSchema = (requiredDocs: IBusinessDocumentDocs[]) =>
//   yup.object().shape<Shape<BusinessDocumentationFormData>>({
//     agentOnboardingKybForm: yup
//       .mixed()
//       .nullable()
//       .required('Please add document')
//       .test(
//         'FILE_FORMAT',
//         'Please check the format of the files selected. The supported formats are jpeg, jpg or png.',
//         (value) => {
//           if (typeof value === 'string') {
//             return true;
//           }

//           if (value) {
//             if (!IMAGE_PDF_SUPPORTED_FORMATS.includes(value?.type)) {
//               return false;
//             }
//           }

//           return true;
//         }
//       )
//       .test('FILE_SIZE', 'File too big. Maximum of 2mb', (value) => {
//         if (typeof value === 'string') {
//           return true;
//         }

//         if (value) {
//           if (value.size > TWO_MB) {
//             return false;
//           }
//         }

//         return true;
//       }),
//     certificateOfIncorporation: yup
//       .mixed()
//       .nullable()
//       .required('Please add document')
//       .test(
//         'FILE_FORMAT',
//         'Please check the format of the files selected. The supported formats are jpeg, jpg or png.',
//         (value) => {
//           if (typeof value === 'string') {
//             return true;
//           }

//           if (value) {
//             if (!IMAGE_PDF_SUPPORTED_FORMATS.includes(value?.type)) {
//               return false;
//             }
//           }

//           return true;
//         }
//       )
//       .test('FILE_SIZE', 'File too big. Maximum of 2mb', (value) => {
//         if (typeof value === 'string') {
//           return true;
//         }

//         if (value) {
//           if (value.size > TWO_MB) {
//             return false;
//           }
//         }

//         return true;
//       }),
//     amlComplianceDocument: yup
//       .mixed()
//       .nullable()
//       .required('Please add document')
//       .test(
//         'FILE_FORMAT',
//         'Please check the format of the files selected. The supported formats are jpeg, jpg or png.',
//         (value) => {
//           if (typeof value === 'string') {
//             return true;
//           }

//           if (value) {
//             if (!IMAGE_PDF_SUPPORTED_FORMATS.includes(value?.type)) {
//               return false;
//             }
//           }

//           return true;
//         }
//       )
//       .test('FILE_SIZE', 'File too big. Maximum of 2mb', (value) => {
//         if (typeof value === 'string') {
//           return true;
//         }

//         if (value) {
//           if (value.size > TWO_MB) {
//             return false;
//           }
//         }

//         return true;
//       }),
//     agreement: yup.boolean().oneOf([true], 'Please kindly agree to the condition'),
//     otherComplianceDocs: yup.array().of(otherDocumentsSchema),
//     einCertificate: yup
//       .mixed()
//       .nullable()
//       .required('Please add document')
//       .test(
//         'FILE_FORMAT',
//         'Please check the format of the files selected. The supported formats are jpeg, jpg or png.',
//         (value) => {
//           if (typeof value === 'string') {
//             return true;
//           }

//           if (value) {
//             if (!IMAGE_PDF_SUPPORTED_FORMATS.includes(value?.type)) {
//               return false;
//             }
//           }

//           return true;
//         }
//       )
//       .test('FILE_SIZE', 'File too big. Maximum of 2mb', (value) => {
//         if (typeof value === 'string') {
//           return true;
//         }

//         if (value) {
//           if (value.size > TWO_MB) {
//             return false;
//           }
//         }

//         return true;
//       }),
//     irsCertificate: yup
//       .mixed()
//       .nullable()
//       .test(
//         'FILE_FORMAT',
//         'Please check the format of the files selected. The supported formats are jpeg, jpg or png.',
//         (value) => {
//           if (typeof value === 'string') {
//             return true;
//           }

//           if (value) {
//             if (!IMAGE_PDF_SUPPORTED_FORMATS.includes(value?.type)) {
//               return false;
//             }
//           }

//           return true;
//         }
//       )
//       .test('FILE_SIZE', 'File too big. Maximum of 2mb', (value) => {
//         if (typeof value === 'string') {
//           return true;
//         }

//         if (value) {
//           if (value.size > TWO_MB) {
//             return false;
//           }
//         }

//         return true;
//       }),
//     certificateOfGoodStanding: yup
//       .mixed()
//       .nullable()
//       .test(
//         'FILE_FORMAT',
//         'Please check the format of the files selected. The supported formats are jpeg, jpg or png.',
//         (value) => {
//           if (typeof value === 'string') {
//             return true;
//           }

//           if (value) {
//             if (!IMAGE_PDF_SUPPORTED_FORMATS.includes(value?.type)) {
//               return false;
//             }
//           }

//           return true;
//         }
//       )
//       .test('FILE_SIZE', 'File too big. Maximum of 2mb', (value) => {
//         if (typeof value === 'string') {
//           return true;
//         }

//         if (value) {
//           if (value.size > TWO_MB) {
//             return false;
//           }
//         }

//         return true;
//       }),
//     businessDba: yup
//       .mixed()
//       .nullable()
//       .test(
//         'FILE_FORMAT',
//         'Please check the format of the files selected. The supported formats are jpeg, jpg or png.',
//         (value) => {
//           if (typeof value === 'string') {
//             return true;
//           }

//           if (value) {
//             if (!IMAGE_PDF_SUPPORTED_FORMATS.includes(value?.type)) {
//               return false;
//             }
//           }

//           return true;
//         }
//       )
//       .test('FILE_SIZE', 'File too big. Maximum of 2mb', (value) => {
//         if (typeof value === 'string') {
//           return true;
//         }

//         if (value) {
//           if (value.size > TWO_MB) {
//             return false;
//           }
//         }

//         return true;
//       })
//     // requiredDocs.forEach((doc) => {
//     //     baseSchema[doc.name] = yup
//     //       .mixed()
//     //       .nullable()
//     //       .test(
//     //         'FILE_FORMAT',
//     //         'Please check the format of the files selected. The supported formats are jpeg, jpg or png.',
//     //         (value) => {
//     //           if (typeof value === 'string') {
//     //             return true;
//     //           }

//     //           if (value) {
//     //             if (!IMAGE_PDF_SUPPORTED_FORMATS.includes(value?.type)) {
//     //               return false;
//     //             }
//     //           }

//     //           return true;
//     //         }
//     //       )
//     //       .test('FILE_SIZE', 'File too big. Maximum of 2mb', (value) => {
//     //         if (typeof value === 'string') {
//     //           return true;
//     //         }

//     //         if (value) {
//     //           if (value.size > TWO_MB) {
//     //             return false;
//     //           }
//     //         }

//     //         return true;
//     //       })
//     //       .test('IS_REQUIRED', `${doc.label} is required`, (value) => {
//     //         if (doc.removeStrict) {
//     //           return true;
//     //         } else {
//     //           if (!value) {
//     //             return false;
//     //           }
//     //         }

//     //         return true;
//     //       });
//     //     // .required(`${doc.label} is required`);
//     //   })
//   });
